import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import { FaStore, FaTags, FaUsers, FaBullseye, FaChartBar, FaRocket, FaCheck } from 'react-icons/fa';
import Swal from 'sweetalert2';

// Importar los modales necesarios
import CreateMarcaModal from '../modals/CreateMarcaModal';
import OnboardingBuyerPersonaModal from './OnboardingBuyerPersonaModal';
import CreateStoreModal from '../modals/CreateStoreModal';
import ChangeObjectiveModal from '../modals/ChangeObjectiveModal';
import QrPdfModal from '../modals/QrPdfModal';
import { useBuyerPersonaQuiz } from '../../hooks/useBuyerPersonaQuiz';
import { useMarketingObjectives } from '../../hooks/useMarketingObjectives';

const OnboardingWizard = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { result: buyerPersonaResult, loading: buyerPersonaLoading, getBuyerPersona } = useBuyerPersonaQuiz();
    const { activeObjective, loading: objectiveLoading, changeActiveObjective } = useMarketingObjectives(user?.userId);

    // Estados para controlar el progreso
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);
    const [data, setData] = useState({
        hasMarca: false,
        hasBuyerPersona: false,
        hasStore: false,
        hasObjective: false,
        selectedStore: null,
        marcas: [],
        storeCount: 0
    });

    // Estados para los modales
    const [showMarcaModal, setShowMarcaModal] = useState(false);
    const [showBuyerPersonaModal, setShowBuyerPersonaModal] = useState(false);
    const [showStoreModal, setShowStoreModal] = useState(false);
    const [showObjectiveModal, setShowObjectiveModal] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);

    // Efecto para cargar el buyer persona
    useEffect(() => {
        if (user?.userId) {
            getBuyerPersona();
        }
    }, [user?.userId, getBuyerPersona]);

    // Efecto para cargar datos iniciales y determinar el primer paso necesario
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!user?.userId || hasInitialDataLoaded) return;

            try {
                const [marcasResponse, storesResponse] = await Promise.all([
                    get('marcas'),
                    get('tiendas')
                ]);

                const storeCount = storesResponse?.length || 0;

                // Verificar si el buyer persona está completo
                const hasBuyerPersona = buyerPersonaResult &&
                    Object.keys(buyerPersonaResult).length > 0 &&
                    buyerPersonaResult.nombre; // Verificar que tenga al menos el nombre

                setData({
                    hasMarca: marcasResponse.length > 0,
                    hasBuyerPersona: hasBuyerPersona,
                    hasStore: storeCount > 0,
                    hasObjective: !!activeObjective,
                    selectedStore: storesResponse?.[0] || null,
                    marcas: marcasResponse,
                    storeCount
                });

                // Determinar el primer paso necesario
                if (!marcasResponse.length) {
                    setCurrentStep(0);
                } else if (!hasBuyerPersona) {
                    setCurrentStep(1);
                } else if (storeCount === 0) {
                    setCurrentStep(2);
                } else if (!activeObjective) {
                    setCurrentStep(3);
                } else {
                    setCurrentStep(4);
                }

                setHasInitialDataLoaded(true);
            } catch (error) {
                console.error('Error fetching initial data:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Hubo un problema al cargar la configuración inicial',
                    icon: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        // Solo cargar datos iniciales cuando tengamos la respuesta del buyer persona
        if (!buyerPersonaLoading) {
            fetchInitialData();
        }
    }, [user?.userId, buyerPersonaResult, activeObjective, hasInitialDataLoaded, buyerPersonaLoading]);

    // Handlers para las acciones de cada paso
    const handleCreateMarca = async (marcaData) => {
        try {
            const response = await post('marcas', marcaData);
            if (response) {
                Swal.fire({
                    title: '¡Marca Creada!',
                    text: 'Tu marca ha sido creada exitosamente',
                    icon: 'success',
                    confirmButtonText: 'Continuar'
                });
                return response;
            }
        } catch (error) {
            console.error('Error creating marca:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo crear la marca. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return null;
        }
    };

    const handleCreateStore = async (storeData) => {
        try {
            // Verificar si ya tiene tiendas
            if (data.storeCount > 0) {
                Swal.fire({
                    title: 'Tienda ya configurada',
                    text: 'Ya tienes una tienda configurada. Continuemos con el siguiente paso.',
                    icon: 'info',
                    confirmButtonText: 'Continuar'
                });
                setCurrentStep(3); // Avanzar al siguiente paso
                return null;
            }

            const response = await post('tiendas', { ...storeData, usuario: user.userId });
            if (response) {
                Swal.fire({
                    title: '¡Tienda Creada!',
                    text: 'Tu tienda ha sido creada exitosamente',
                    icon: 'success',
                    confirmButtonText: 'Continuar'
                });
                return response;
            }
        } catch (error) {
            console.error('Error creating store:', error);

            // Si el error es por límite de tiendas, manejarlo de forma especial
            if (error.status === 409) {
                Swal.fire({
                    title: 'Tienda ya configurada',
                    text: 'Ya tienes una tienda configurada. Continuemos con el siguiente paso.',
                    icon: 'info',
                    confirmButtonText: 'Continuar'
                });
                setCurrentStep(3); // Avanzar al siguiente paso
                return null;
            }

            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo crear la tienda. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return null;
        }
    };

    const handleSetObjective = async (objectiveData) => {
        try {
            const response = await changeActiveObjective(objectiveData);
            if (response) {
                Swal.fire({
                    title: '¡Objetivo Establecido!',
                    text: 'Tu objetivo ha sido establecido exitosamente',
                    icon: 'success',
                    confirmButtonText: 'Continuar'
                });
                return response;
            }
        } catch (error) {
            console.error('Error setting objective:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo establecer el objetivo. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
            return null;
        }
    };

    const steps = [
        {
            title: "Crea tu Marca",
            description: "¡Petit o grande, toda marca necesita brillar! Configura la tuya y prepárate para destacar",
            icon: <FaTags className="text-blue-500 text-2xl" />,
            action: () => !data.hasMarca && setShowMarcaModal(true),
            isCompleted: data.hasMarca
        },
        {
            title: "Conoce a tu Público",
            description: "¿Quién es esa persona que necesita lo que vendes? ¡Vamos a descubrirlo juntos!",
            icon: <FaUsers className="text-green-500 text-2xl" />,
            action: () => !data.hasBuyerPersona && setShowBuyerPersonaModal(true),
            isCompleted: data.hasBuyerPersona
        },
        {
            title: "Tu Base de Operaciones",
            description: "Configura tu local, ¡el cuartel general desde donde conquistarás el mercado!",
            icon: <FaStore className="text-purple-500 text-2xl" />,
            action: () => !data.hasStore && setShowStoreModal(true),
            isCompleted: data.hasStore
        },
        {
            title: "Define tu Meta",
            description: "¿Qué quieres lograr? ¡Apunta alto, tu copiloto de marketing te ayudará a llegar!",
            icon: <FaBullseye className="text-red-500 text-2xl" />,
            action: () => !data.hasObjective && setShowObjectiveModal(true),
            isCompleted: data.hasObjective
        },
        {
            title: "¡Hora de Crecer!",
            description: "Conecta con tus clientes y alimenta a PetitPrime con datos. ¡Cuanta más información recopiles, mejores estrategias y resultados obtendrás!",
            icon: <FaChartBar className="text-indigo-500 text-2xl" />,
            action: () => setShowQrModal(true),
            isCompleted: false
        }
    ];

    const handleComplete = async () => {
        try {
            // Guardar en localStorage que el onboarding está completado
            localStorage.setItem('onboardingCompleted', 'true');
            localStorage.setItem('onboardingCompletedAt', new Date().toISOString());

            // Mostrar mensaje de éxito
            await Swal.fire({
                title: '¡Despegamos! 🚀',
                text: '¡Tu copiloto de marketing está listo! Prepárate para estrategias personalizadas, insights poderosos y un marketing que hará que tu negocio brille. ¡Aunque seamos Petit, vamos a hacer cosas grandes juntos!',
                icon: 'success',
                confirmButtonText: '¡A por Todas!',
                allowOutsideClick: false
            });

            // Redirigir al dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error('Error completing onboarding:', error);
            Swal.fire({
                title: 'Error',
                text: 'Hubo un problema al finalizar la configuración. Por favor, inténtalo de nuevo.',
                icon: 'error',
                confirmButtonText: 'Entendido'
            });
        }
    };

    // Mostrar loading mientras se verifica todo
    if (loading || buyerPersonaLoading || objectiveLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Verificando configuración...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <FaRocket className="mx-auto h-12 w-12 text-blue-500" />
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        ¡Prepara tu Petit Negocio para Grandes Resultados! 🚀
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Tu copiloto de marketing está listo para despegar. ¡Configura estos detalles y prepárate para conquistar el mercado!
                    </p>
                </div>

                {/* Progress Steps */}
                <div className="mb-8">
                    <div className="flex items-center justify-between relative">
                        {steps.map((step, index) => (
                            <div
                                key={index}
                                className={`flex flex-col items-center relative z-10 ${index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                                    }`}
                            >
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${step.isCompleted
                                        ? 'bg-green-100 text-green-600'
                                        : index <= currentStep
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'bg-gray-100 text-gray-400'
                                        }`}
                                >
                                    {step.isCompleted ? <FaCheck /> : step.icon}
                                </div>
                                <p className="mt-2 text-xs font-medium">
                                    {step.title}
                                    {step.isCompleted && <span className="text-green-600 ml-1">(Completado)</span>}
                                </p>
                            </div>
                        ))}
                        {/* Progress line */}
                        <div
                            className="absolute top-5 h-0.5 bg-gray-200 left-0 right-0 -z-10"
                            style={{
                                width: '100%',
                                transform: 'translateY(-50%)'
                            }}
                        >
                            <div
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{
                                    width: `${(currentStep / (steps.length - 1)) * 100}%`
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Current Step Content */}
                <div className="bg-white shadow rounded-lg p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 flex items-center mb-4">
                        {steps[currentStep].icon}
                        <span className="ml-2">{steps[currentStep].title}</span>
                    </h3>
                    <p className="text-gray-600 mb-6">{steps[currentStep].description}</p>

                    {/* Botón de acción */}
                    {currentStep === steps.length - 1 && data.hasStore && data.hasBuyerPersona && data.hasMarca ? (
                        // Si es el último paso y todo está configurado, mostrar botón de finalizar
                        <button
                            onClick={handleComplete}
                            className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                        >
                            <FaRocket className="mr-2" />
                            Finalizar y Comenzar
                        </button>
                    ) : (
                        // Si no, mostrar el botón normal del paso actual
                        <button
                            onClick={steps[currentStep].action}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                            {steps[currentStep].icon}
                            <span className="ml-2">Configurar Ahora</span>
                        </button>
                    )}
                </div>

                {/* Modales */}
                {showMarcaModal && (
                    <CreateMarcaModal
                        isOpen={showMarcaModal}
                        onClose={() => setShowMarcaModal(false)}
                        onSubmit={async (marcaData) => {
                            const result = await handleCreateMarca(marcaData);
                            if (result) {
                                setData(prev => ({ ...prev, hasMarca: true, marcas: [...prev.marcas, result] }));
                                // No avanzamos aquí, lo haremos después de manejar el logotipo
                            }
                            return result;
                        }}
                        onUploadLogotipo={async (marcaId, file) => {
                            const formData = new FormData();
                            formData.append('logotipo', file);

                            try {
                                const response = await post(`marcas/${marcaId}/logotipo`, formData, {
                                    headers: {
                                        'Content-Type': 'multipart/form-data',
                                    },
                                });
                                // Solo avanzamos al siguiente paso después de completar todo el proceso
                                setCurrentStep(1);
                                return response;
                            } catch (error) {
                                console.error('Error uploading logo:', error);
                                throw error;
                            }
                        }}
                        onComplete={() => {
                            // Esta función se llamará cuando el usuario decida no subir logo
                            // o cuando se complete todo el proceso
                            setCurrentStep(1);
                            setShowMarcaModal(false);
                        }}
                        user={user}
                    />
                )}

                {showBuyerPersonaModal && (
                    <OnboardingBuyerPersonaModal
                        isOpen={showBuyerPersonaModal}
                        onClose={() => setShowBuyerPersonaModal(false)}
                        onComplete={(result) => {
                            if (result) {
                                setData(prev => ({ ...prev, hasBuyerPersona: true }));
                                setCurrentStep(2);
                            }
                            setShowBuyerPersonaModal(false);
                        }}
                    />
                )}

                {showStoreModal && (
                    <CreateStoreModal
                        isOpen={showStoreModal}
                        onClose={() => setShowStoreModal(false)}
                        onSubmit={async (storeData) => {
                            const result = await handleCreateStore(storeData);
                            if (result) {
                                setData(prev => ({
                                    ...prev,
                                    hasStore: true,
                                    selectedStore: result
                                }));
                                setCurrentStep(3);
                            }
                            setShowStoreModal(false);
                        }}
                        marcas={data.marcas}
                    />
                )}

                {showObjectiveModal && (
                    <ChangeObjectiveModal
                        isOpen={showObjectiveModal}
                        onClose={() => setShowObjectiveModal(false)}
                        onObjectiveChange={async (objectiveData) => {
                            const result = await handleSetObjective(objectiveData);
                            if (result) {
                                setData(prev => ({ ...prev, hasObjective: true }));
                                setCurrentStep(4);
                            }
                            setShowObjectiveModal(false);
                        }}
                    />
                )}

                {showQrModal && data.selectedStore && (
                    <QrPdfModal
                        tiendaId={data.selectedStore._id}
                        onClose={() => {
                            setShowQrModal(false);
                            handleComplete();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default OnboardingWizard; 